import * as React from 'react';
import classNames from 'classnames';
import { MediaSet } from '@/types/media';
import { Image } from '../Image';
import { Link } from '../Link';
import { LoadingBar } from '../LoadingBar';
import { TextBadge } from '../TextBadge';
import styles from './BlogCard.module.scss';

/** The design shows at most three labels on a card */
const maxLabels = 3;

export interface BlogCardProps {
	readonly className?: string;
	/** Renders the card as a placeholder, so a loading grid keeps the real card geometry */
	readonly isSkeleton?: boolean;
	readonly image?: MediaSet;
	readonly altText?: string;
	readonly url?: string;
	readonly headline: string;
	readonly description?: string;
	readonly labels?: readonly string[];
	readonly authorName?: string;
	/** Already formatted for the current culture */
	readonly publicationDate?: string;
	/** Free text from the CMS, e.g. "3 min" */
	readonly readingTime?: string;
	readonly readMoreLabel: string;
	readonly lazyLoading?: boolean;
}

/**
 * Generic BlogCard component, used for showing an article in an overview, e.g. the Knowledge Hub.
 */
export const BlogCard: React.FunctionComponent<BlogCardProps> = ({
	className,
	isSkeleton,
	image,
	altText,
	url,
	headline,
	description,
	labels,
	authorName,
	publicationDate,
	readingTime,
	readMoreLabel,
	lazyLoading,
}) => {
	// Skipping the empty ones keeps a missing author or date from leaving a stray separator
	const metaText = [authorName, publicationDate, readingTime].filter(Boolean).join(' | ');
	const visibleLabels = labels?.slice(0, maxLabels) ?? [];
	if (isSkeleton) {
		return (
			<article className={classNames(styles.wrapper, className)} aria-hidden={'true'}>
				{/* The image box already carries the aspect ratio and a neutral fill */}
				<div className={styles.imageWrapper} />
				<div className={styles.content}>
					<LoadingBar width={'40%'} height={'24px'} />
					<div className={styles.headlineGroup}>
						<LoadingBar height={'28px'} />
						<LoadingBar width={'70%'} height={'28px'} />
						<LoadingBar width={'55%'} height={'14px'} />
					</div>
					<LoadingBar height={'20px'} />
					<LoadingBar width={'80%'} height={'20px'} />
					<LoadingBar className={styles.ctaSkeleton} width={'114px'} height={'48px'} />
				</div>
			</article>
		);
	}

	return (
		<article className={classNames(styles.wrapper, className)}>
			<div className={styles.imageWrapper}>
				<Image
					className={styles.image}
					{...image}
					responsiveSizesSettings={'(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw'}
					altText={image?.altText || altText || ''}
					lazyLoading={lazyLoading}
				/>
			</div>
			<div className={styles.content}>
				{visibleLabels.length > 0 ? (
					<ul className={styles.labels}>
						{visibleLabels.map((label) => (
							<li key={label}>
								<TextBadge className={styles.label} text={label} />
							</li>
						))}
					</ul>
				) : (
					// Holds the row open, so headlines line up across a row of cards
					<div className={styles.labels} aria-hidden={'true'} />
				)}
				<div className={styles.headlineGroup}>
					<p className={styles.headline}>
						{/* The link stretches across the card, so the whole card is clickable
						    while staying a single link named after the article */}
						<Link className={styles.headlineLink} linkUrl={url || ''} target={'_self'}>
							{headline}
						</Link>
					</p>
					{metaText && <p className={styles.meta}>{metaText}</p>}
				</div>
				{description && <p className={classNames(styles.description, 'u-ellipsis-3-lines')}>{description}</p>}
				{/* Decorative: the stretched headline link already makes the card clickable */}
				<span className={styles.cta} aria-hidden={'true'}>
					{readMoreLabel}
				</span>
			</div>
		</article>
	);
};
